/**
 * usePropertySalesInfo — the property slice's thin sales-info projection
 * hook (SCRUM-337).
 *
 * A DELIBERATE, minimal cross-slice read surface (the SCRUM-334
 * `usePropertyNameLookup` precedent): the booking create-reservation form's
 * `SalesInfoPanel` needs a property's display name + occupancy capacity to
 * orient the agent while they build a reservation — WITHOUT teaching the
 * booking BC how to fetch or decode a property (BC boundary honesty; booking
 * does not own property display). The form consumes this via the sanctioned
 * property barrel export.
 *
 * This hook reads the property DETAIL endpoint
 * `GET /organizations/{slug}/properties/{id}` and projects ONLY what the
 * panel needs off `PropertyResponseDto`:
 *   - `name`     — `websiteNameOverride ?? unitName` (the display-name
 *                  precedence used everywhere a property is referenced;
 *                  the internal-friendly override wins over the formal
 *                  `unitName` when set).
 *   - `capacity` — `{ maxSleeps, maxAdults, bedrooms, bathrooms }`.
 *                  `PropertyCapacityDto` exposes NO single `bathrooms`
 *                  field — it exposes four bathroom sub-counts
 *                  (`bathroomsFull` + `bathroomsHalf` + `bathroomsShower` +
 *                  `bathroomsBathtub`). The panel wants a single total, so
 *                  the sub-counts are SUMMED here. This is display-count
 *                  arithmetic on plain integers — NOT money (CLAUDE.md rule
 *                  6 governs `Cents`; a bathroom count is not `Cents`).
 *
 * Status union: `loading | ready | forbidden | error`.
 *   - `ready`     — 200 → the projected `{ name, capacity }`.
 *   - `loading`   — no org slug OR `propertyId` null/empty → query disabled.
 *   - `forbidden` — 403 → HONEST forbidden (the caller lost `property.read`
 *                    since `/auth/me` cached); the panel shows a
 *                    permission-denied note, NOT a broken card. Not folded
 *                    into the generic error branch.
 *   - `error`     — any other non-ok status.
 *
 * Sentinel: the queryFn returns `FORBIDDEN` on 403; everything else non-ok /
 * undefined-body throws → `error`. `retry: false`.
 */

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import type { components } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { propertyQueryKeys } from './queryKeys.js';

type PropertyResponseDto = components['schemas']['PropertyResponseDto'];

/**
 * 403 sentinel — the caller lost `property.read` since `/auth/me` cached.
 * The panel renders a permission-denied note (no Retry). Same idiom as
 * `use-property-name-lookup.ts`.
 */
const FORBIDDEN = Symbol('property-sales-info:forbidden');

/** The occupancy capacity the sales-info panel renders. */
export interface SalesInfoCapacity {
  readonly maxSleeps: number;
  readonly maxAdults: number;
  readonly bedrooms: number;
  /** Total bathrooms (sum of the four `PropertyCapacityDto` sub-counts). */
  readonly bathrooms: number;
}

/** The projected sales-info data. */
export interface SalesInfoData {
  /** `websiteNameOverride ?? unitName`. */
  readonly name: string;
  readonly capacity: SalesInfoCapacity;
}

export type UsePropertySalesInfoResult =
  | { readonly status: 'loading'; readonly data?: undefined }
  | { readonly status: 'ready'; readonly data: SalesInfoData }
  | { readonly status: 'forbidden'; readonly data?: undefined }
  | { readonly status: 'error'; readonly data?: undefined };

/**
 * The display name for a property: `websiteNameOverride` (the
 * internal-friendly override) when set, else the formal `unitName`. Matches
 * the precedence used everywhere the property is referenced.
 */
function propertyDisplayName(dto: PropertyResponseDto): string {
  const override = dto.websiteNameOverride;
  if (typeof override === 'string' && override.trim().length > 0) {
    return override;
  }
  return dto.unitName;
}

/** Project `PropertyResponseDto → SalesInfoData` (name + capacity). */
function projectSalesInfo(dto: PropertyResponseDto): SalesInfoData {
  const cap = dto.capacity;
  return {
    name: propertyDisplayName(dto),
    capacity: {
      maxSleeps: cap.maxSleeps,
      maxAdults: cap.maxAdults,
      bedrooms: cap.bedrooms,
      // No single `bathrooms` field on the wire — sum the four sub-counts.
      bathrooms:
        cap.bathroomsFull +
        cap.bathroomsHalf +
        cap.bathroomsShower +
        cap.bathroomsBathtub,
    },
  };
}

export function usePropertySalesInfo(
  propertyId: string | null,
): UsePropertySalesInfoResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const enabled =
    orgSlug !== null && propertyId != null && propertyId.length > 0;

  const query = useQuery({
    // `detail(id)` requires a string; when id is null/empty the query is
    // disabled so the key is never used — an empty string keeps the tuple
    // type stable. Tagged with `sales-info` so this projection-specific
    // entry is distinct from the full detail hook's entry yet still
    // invalidates under the property detail root.
    queryKey: [
      ...propertyQueryKeys.detail(propertyId ?? ''),
      'sales-info',
      orgSlug,
    ] as const,
    enabled,
    retry: false,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/properties/{id}',
        {
          params: {
            path: { slug: orgSlug as string, id: propertyId as string },
          },
        },
      );
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/properties/${propertyId} failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  // Project only when ready. Memoised on the query data reference so the
  // projection is stable across re-renders that don't change the DTO.
  const data = React.useMemo<SalesInfoData | null>(() => {
    const raw = query.data;
    if (raw === undefined || raw === FORBIDDEN) {
      return null;
    }
    // The property detail endpoint GET /organizations/{slug}/properties/{id}
    // returns the PropertyResponseDto DIRECTLY (no `{ data }` envelope — see the
    // controller's `toPropertyDetailResponse`, and the sibling `use-property-detail`
    // hook which reads `result.data`). `raw` IS the DTO; do not double-unwrap.
    return projectSalesInfo(raw);
  }, [query.data]);

  if (!enabled || query.isPending) {
    return { status: 'loading' };
  }
  if (query.isError) {
    return { status: 'error' };
  }
  if (query.data === FORBIDDEN || data === null) {
    return { status: 'forbidden' };
  }

  return { status: 'ready', data };
}
